import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useReducer } from "react";

import {
  getTimeReportProjectItemPathId,
  TimeReportProjectItemAggregate,
  validateDailyFormattedTime,
  validateHourlyFormattedTime,
} from "@/data/time-reports";
import { noop } from "@/utils/functions";

import { useTimeReportEdit } from "../../edit/TimeReportEditContext";
import {
  timeReportCalendarItemCellInitialState,
  timeReportCalendarItemCellReducer,
} from "./timeReportCalendarItemCellReducer";

export const useTimeReportCalendarItemCell = (item: TimeReportProjectItemAggregate) => {
  const [state, dispatch] = useReducer(timeReportCalendarItemCellReducer, timeReportCalendarItemCellInitialState);

  const { itemsStatus, updateCalendarItem } = useTimeReportEdit();

  const validate = useMemo(
    () => (item.measureUnit === "HOURLY" ? validateHourlyFormattedTime : validateDailyFormattedTime),
    [item],
  );

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "SET_VALUE", value: e.currentTarget.value });
  }, []);

  const handleFocus = useCallback(() => {
    dispatch({ type: "SET_VALUE", value: item.loggedTime.isEmpty ? "" : item.loggedTime.formattedAmount });
    dispatch({ type: "SET_STATUS", status: "focus" });
  }, [item]);

  const handleBlur = useCallback(() => {
    dispatch({ type: "SET_STATUS", status: "blur" });

    validate(
      state.value,
      result => {
        if (result.reportedTime !== item.loggedTime.reportedTime) {
          updateCalendarItem(item.itemPath, i => ({
            ...i,
            reportedTime: result.reportedTime,
            comment: null,
            attachments: null,
          }));
        }
      },
      noop,
    );
  }, [item, state, updateCalendarItem, validate]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !state.isValid) {
        e.preventDefault();
      }

      if (e.key === "Escape") {
        e.stopPropagation();
      }
    },
    [state.isValid],
  );

  useEffect(() => {
    if (state.status === "focus") {
      validate(
        state.value,
        () => {
          dispatch({ type: "SET_IS_VALID", isValid: true });
        },
        error => {
          dispatch({ type: "SET_IS_VALID", isValid: false, error });
        },
      );
    }
  }, [state.value, state.status, validate, updateCalendarItem, item]);

  return {
    ...state,
    itemStatus: itemsStatus[getTimeReportProjectItemPathId(item.itemPath)],
    handleChange,
    handleFocus,
    handleBlur,
    handleKeyDown,
  };
};
